import { Component, OnInit, inject, Input, ViewChild, TemplateRef, HostListener, Output, EventEmitter, signal, input } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { faQuestion, faTableList, faTableCellsLarge, faPrint, faFileExcel } from '@fortawesome/pro-solid-svg-icons';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { DateRange } from '../../models/date-range';
import { PcgTableColumn } from '../pcg-table-column.interface';
import { ButtonFilterService } from './button-filter/button-filter.service';
import { GlobalService } from './../../service/global.service';
import { UnitType } from '../table.component';
import { InventoryType } from '../table.component';
import { TableTopLegendVm } from './table-top-legend/table-top-legend.component';
import { HttpClient } from '@angular/common/http';
import { SelectItem } from '../../models/select-item';
import { GlobalVariablesService } from '../../../services/global-variables.service';
import { PermissionRowIds } from '../../../core/security/permission-profiles';

@Component({
    selector: 'pcg-table-top',
    templateUrl: './table-top.component.html',
    styleUrls: ['./table-top.component.scss'],
    standalone: false
})
export class TableTopComponent implements OnInit {

    gvs = inject(GlobalVariablesService);
    public btnFilterService = inject(ButtonFilterService);
	public modalService = inject(NgbModal);
    private http = inject(HttpClient);

	@ViewChild('tooltipModal', { static: true }) tooltipModal!: TemplateRef<any>;

	@Input() exactMatchSource!: BehaviorSubject<boolean>;
	@Input() filterSource!: BehaviorSubject<string>;
	@Input() columnDefs!: Map<string, PcgTableColumn>;
	@Input() canGlobalSearch = true;
	@Input() canExactMatchSearch = true;
	@Input() canShowHideColumns = true;
	@Input() ajaxData: any;
	@Input() pageSizeSource!: BehaviorSubject<number>;
	@Input() pageLengths!: [number];
	@Input() table: any;
	@Input() canExportTableToExcel = false;
	@Input() canPrintTable = false;
	@Input() hasTooltips = false;
	@Input() tooltipContent!: string;
	@Input() canToggleUnits = false;
	@Input() canToggleInventoryTypes = false;
	@Input() canToggleUserPermissions = false;
    @Input() canToggleDepartments = false;
	@Input() showTilesBtn = false;
	@Input() showTooltipsBtn = false;
	@Input() tableViewStorage = "";
    @Input() legend: TableTopLegendVm[] = null!;	
	// Inputs for implementing the table dropdown filter features
	/** Filter ID to implement table dropdown filters. Passed from table component */
	@Input() filterId: string = null!;
	/** Pass through a filtermap to show filters being utilized. Ex. src\app\shared\business-areas\order-list\order-list */
    filterMap = input(null)
	/** Filters utilized for the table dropdown filters. Passed from table component */
	@Input() filters: any;
	/** Filter array for special reset button visibility behavior. Passed from table component */
	@Input() emptyFilters: any = null!;
	/** Whether the table dropdown filters have reset capabilities. Passed from table component*/
	@Input() canResetFilters = true;
	/* map of string and and function pointer key value pairs where the key is the key to the filter and the value is a function return a boolean of whether 
	or not to show reset filters*/
	@Input() customResetFiltersMap!: Map<string, (args: any) => boolean>;

	@Output() isUsingTiles = new EventEmitter<boolean>();
    @Output() reloadTable = new EventEmitter<any>()

	// Get a unique id to append to the id of controls to make it distinct
	// Doing this to have a distinct id to add to the for attribute of the exact match label
	uniqueId = `${new Date().getDay()}-${new Date().getSeconds()}-${new Date().getMilliseconds()}`;
	unitType: UnitType = UnitType.Vials;
	unitTypeEnum = UnitType;
	inventoryType: InventoryType = null!;
	inventoryTypeEnum = InventoryType;
	// S2N-3076: the user-permission toggle selects a Permissions ROW ID, so it needs its own
	// selected value — it was reading the inventory toggle's, which is a different enum entirely.
	permissionRowId: number = null!;
	permissionRowIds = PermissionRowIds;
    departments!: SelectItem[];
    selectedDepartment!: SelectItem;

	faIconName = { faQuestion, faTableList, faTableCellsLarge, faPrint, faFileExcel };

	isMobile = signal<boolean>(false);
	@HostListener('window:resize') onResize() { this.isMobile.set(GlobalService.setIsMobile(window.innerWidth)); }

	ngOnInit() {
		if (this.canResetFilters) { this.emptyFilters ??= { ...this.filters }; }
		this.isMobile.set(GlobalService.setIsMobile(window.innerWidth));
        if (this.canToggleDepartments) { this.getDepartments(); }
	}

	setUnitType(unitType: UnitType) {
		this.unitType = unitType;
		this.table.onToggleShowVials(this.unitType);
	}	

	showResetFilterBtn() {
		if (this.customResetFiltersMap != null) {
			return this.canResetFilters 
				&& Object.entries(this.filters).filter(([key, val]) => (
					this.customResetFiltersMap?.has(key) 
					&& this.customResetFiltersMap?.get(key)!(val)
				)
			)?.length;
		}

		return this.canResetFilters 
			&& Object.entries(this.filters).filter(([key, val]) => (
				key in this.emptyFilters 
				&& val !== false 
				&& !(
					this.emptyFilters[key] === val
					|| (
						// Checking dateranges due to clearing range sets value to an object.
						this.emptyFilters[key] instanceof DateRange 
							&& this.emptyFilters[key].dateBegin === (val!['dateBegin'] ?? null) 
							&& this.emptyFilters[key].dateEnd === (val!['dateEnd'] ?? null)
					)
				)
			))?.length;
	}

    getDepartments() {
        this.http.get<SelectItem[]>("api/select/departments").subscribe((departments: SelectItem[]) => {
            this.departments = departments
        })
    }

    setDepartmentId(departmentId: number) {
        this.ajaxData.departmentId = Number(departmentId);
        this.reloadTable.emit();
    }

    /**
     * S2N-3076: `permissionLevel` is a Permissions row id (PermissionRowIds), null meaning "All".
     * No server endpoint binds the parameter today — the association-list user tables filter by
     * profile through the table's SEARCH TEXT (see the standalone available/associated lists) — so
     * this reloads the table and nothing else. Naming the ids keeps the next reader from taking
     * them for InventoryType values, which is how the old [value] bindings got written.
     */
    setPermissionLevel(permissionLevel: number) {
        this.permissionRowId = permissionLevel;
        this.ajaxData.permissionLevel = permissionLevel;
        this.reloadTable.emit();
    }

    clearSearch(event: any) { setTimeout(() => { if (event.target.value == "") { this.filterSource.next(""); } }, 1); }
	tooltipButtonClicked() { this.modalService.open(this.tooltipModal); }
	tableViewClick(isTiles: boolean) { this.isUsingTiles.emit(isTiles); }

}